/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: requestconfig.ts
 * Class: requestconfig.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import { AxiosRequestConfig } from 'axios';
import Vue from 'vue';
import { getLocalePrefixFromRoute, getRegionPrefixFromRoute } from '../router/utils';

/**
 * global configuration for any ajax request used by vue
 */
export const defaultRequestConfig: AxiosRequestConfig = {
  timeout: 30000, // --- time to life for a request
  maxRedirects: 1, // --- how many times we want to retry the request

  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'X-Application-Name': 'VueJS',
  },
};

/**
 * enumeration for all available controllers in the vue folder
 */
export const enum ApiEndpointEnum {
  Payment = 0,
  Donation = 1,
  News = 2,
  Game = 3,
  ConfigurationAPI = 4,
  InterfaceAPI = 5,
  OrderAPI = 6,
  OrderActivate = 7,
  Gamecloud = 8,
  ProductAPI = 9,
  CloudAPI = 10,
  TransactionAPI = 11,
  AuthAPI = 12,
  PaymentAPI = 13,
  TextPage = 14,
  Navigation = 15,
  Invoices = 16
}

/**
 * add the base url here, we will automatically concatenate the region prefix (if it does exists)
 *
 * region-prefix(automatically prepended)/cake-action-url/parameters (if required)
 */
const routeRequestMap: Map<ApiEndpointEnum, string> = new Map([
  [ApiEndpointEnum.Payment, '/{regionPrefix}/vue_j_s/VuePayment/'],
  [ApiEndpointEnum.News, '/{regionPrefix}/vue_j_s/VueNews/'],
  [ApiEndpointEnum.TextPage, '/{regionPrefix}/vue_j_s/VueTextPage/'],
  [ApiEndpointEnum.Game, '/{regionPrefix}/vue_j_s/VueGame/'],
  [ApiEndpointEnum.ConfigurationAPI, '/{localePrefix}/api/configurations/'],
  [ApiEndpointEnum.ProductAPI, '/{localePrefix}/api/product/'],
  [ApiEndpointEnum.InterfaceAPI, '/{regionPrefix}/api/'],
  [ApiEndpointEnum.OrderAPI, '/{regionPrefix}/order/basket'],
  [ApiEndpointEnum.Donation, '/{regionPrefix}/vue_j_s/VueDonation/'],
  [ApiEndpointEnum.OrderActivate, '/{regionPrefix}/order/ajaxActivate'],
  [ApiEndpointEnum.Gamecloud, '/{regionPrefix}/gamecloud'],
  [ApiEndpointEnum.CloudAPI, '/{regionPrefix}/cloud/'],
  [ApiEndpointEnum.TransactionAPI, '/{regionPrefix}/profile/'],
  [ApiEndpointEnum.AuthAPI, '/{regionPrefix}/auth/'],
  [ApiEndpointEnum.PaymentAPI, '/{regionPrefix}/order/payment_api/'],
  [ApiEndpointEnum.Navigation, '/{regionPrefix}/menu'],
  [ApiEndpointEnum.Invoices, '/{regionPrefix}/invoices'],
]);

/**
 * will automatically return a requestConfig object
 * with the correct base url including the regionPrefix
 *
 * @param endpoint
 */
export function getApiRequestConfig(endpoint: ApiEndpointEnum): AxiosRequestConfig {
  const requestConfig: AxiosRequestConfig = { ...defaultRequestConfig };

  const mapPrefix = {
    '{regionPrefix}': getRegionPrefixFromRoute() ?? Vue.prototype.$api.data.routes.regionPrefix,
    '{localePrefix}': getLocalePrefixFromRoute() ?? Vue.prototype.$api.data.routes.localePrefix,
  };

  requestConfig.baseURL = routeRequestMap
    .get(endpoint)
    .replace(/{regionPrefix}|{localePrefix}/gi, (matched) => mapPrefix[matched]);

  return requestConfig;
}
